import { useEffect, useState } from 'react';

export type ScrollDirection = 'up' | 'down' | null;

interface UseScrollDirectionOptions {
  /** Minimum delta in pixels before we consider it a real direction change (to avoid jitter). */
  threshold?: number;
}

/**
 * Track vertical scroll direction of the window.
 * Returns 'down', 'up', or null (initial / unchanged).
 */
export function useScrollDirection(options: UseScrollDirectionOptions = {}): ScrollDirection {
  const { threshold = 8 } = options;
  const [direction, setDirection] = useState<ScrollDirection>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    let lastY = window.scrollY;

    const handleScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY;
      if (Math.abs(delta) < threshold) return;

      setDirection((prev) => {
        const next: ScrollDirection = delta > 0 ? 'down' : 'up';
        return prev === next ? prev : next;
      });

      lastY = y;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  return direction;
}

